import React, { useState } from 'react';
import {
    CalendarIcon,
    LocationIcon,
    TimeIcon,
    InfoIcon,
    CloseIcon,
    EditIcon as Edit2
} from './Icons';
import InlineFieldEditor, { EditButton } from './InlineFieldEditor';
import {
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    IconButton,
    useTheme,
    alpha
} from '@mui/material';

const MAIN_URL = process.env.REACT_APP_MAIN_URL || 'https://pfadfinder-wutoeschingen.org';

const EventCard = ({ event, isEditable = false, onUpdate = () => {} }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [editingField, setEditingField] = useState(null); // 'title', 'date', 'startTime', 'endTime', 'location', 'category', 'description'
    const theme = useTheme();

    const getCategoryColor = (category) => {
        switch (category) {
            case 'Gruppenstunde': return 'success';
            case 'Lager': return 'warning';
            case 'Aktion': return 'secondary';
            default: return 'primary';
        }
    };

    const formatDate = (dateString) => {
        const options = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('de-DE', options);
    };

    const categoryColor = getCategoryColor(event.category);

    const handleUpdateField = (field, value) => {
        onUpdate(field, value);
    };

    return (
        <>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: 'rgba(var(--color-surface-container), 1)',
                    color: 'rgba(var(--color-surface-on), 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgba(var(--color-outline-variant), 0.45)',
                    boxShadow: '0 1px 12px rgba(15, 23, 42, 0.08)',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                        backgroundColor: 'rgba(var(--color-surface-container-high), 1)',
                        borderColor: 'rgba(var(--color-outline-variant), 0.65)',
                    },
                }}
                onClick={() => {
                    if (event.slug) {
                        window.open(`${MAIN_URL}/events/${event.slug}`, '_blank', 'noopener,noreferrer');
                    } else if (event.link) {
                        window.open(event.link, '_blank', 'noopener,noreferrer');
                    } else {
                        setIsExpanded(true);
                    }
                }}
            >
                {/* Category accent bar at the top */}
                <Box
                    sx={{
                        height: 6,
                        width: '100%',
                        bgcolor: (theme) => theme.palette[categoryColor].main,
                        opacity: 0.8
                    }}
                />

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <div className="flex items-center gap-2">
                            <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider ${
                                event.category === 'Lager'
                                    ? 'bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40'
                                    : event.category === 'Gruppenstunde'
                                    ? 'bg-emerald-500/20 text-emerald-900 dark:text-emerald-200 border border-emerald-500/40'
                                    : event.category === 'Aktion'
                                    ? 'bg-sky-500/20 text-sky-900 dark:text-sky-200 border border-sky-500/40'
                                    : 'bg-primary/20 text-primary border border-primary/40'
                            }`}>
                                {event.category || 'Event'}
                            </span>
                            {isEditable && <EditButton onClick={() => setEditingField('category')} />}
                        </div>
                        {event.isRegistrationEnabled && (
                            <div className="px-3 py-1 rounded-full bg-gradient-to-r from-primary via-secondary to-tertiary text-white text-[10px] font-bold shadow-sm">
                                Anmeldung offen
                            </div>
                        )}
                        {event.link && (
                            <IconButton
                                size="small"
                                href={event.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    color: 'rgba(var(--color-surface-on-variant), 0.6)',
                                    '&:hover': {
                                        color: (theme) => theme.palette[categoryColor].main,
                                        bgcolor: (theme) => alpha(theme.palette[categoryColor].main, 0.08)
                                    }
                                }}
                            >
                                <InfoIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        )}
                    </Box>

                    {isEditable && editingField === 'title' ? (
                        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
                            <InlineFieldEditor 
                                label="Titel" 
                                value={event.title} 
                                onSave={(val) => { handleUpdateField('title', val); setEditingField(null); }}
                                onCancel={() => setEditingField(null)}
                            />
                        </div>
                    ) : (
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                minHeight: '2.8em',
                                lineHeight: 1.3,
                                color: 'rgba(var(--color-surface-on), 1)',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {event.title}
                            {isEditable && <EditButton onClick={() => setEditingField('title')} />}
                        </Typography>
                    )}

                    <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'rgba(var(--color-surface-on-variant), 0.8)' }}>
                            <Box sx={{ bgcolor: (theme) => alpha(theme.palette[categoryColor].main, 0.1), p: 0.6, borderRadius: 1, display: 'flex' }}>
                                <CalendarIcon sx={{ fontSize: 16, color: (theme) => theme.palette[categoryColor].main }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(event.date)}</Typography>
                            {isEditable && <EditButton onClick={() => setEditingField('date')} />}
                        </Box>

                        {isEditable && editingField === 'date' && (
                            <div className="my-2" onClick={(e) => e.stopPropagation()}>
                                <InlineFieldEditor 
                                    label="Datum" 
                                    type="date"
                                    value={event.date?.split('T')[0]} 
                                    onSave={(val) => { handleUpdateField('date', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}

                        {(event.startTime || event.endTime || isEditable) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'rgba(var(--color-surface-on-variant), 0.8)' }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)', p: 0.6, borderRadius: 1, display: 'flex' }}>
                                    <TimeIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {event.startTime || '--:--'} {event.endTime ? `- ${event.endTime}` : ''} Uhr
                                </Typography>
                                {isEditable && <EditButton onClick={() => setEditingField('time')} />}
                            </Box>
                        )}

                        {isEditable && editingField === 'time' && (
                            <div className="my-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                                <InlineFieldEditor 
                                    label="Startzeit" 
                                    type="time"
                                    value={event.startTime || ''} 
                                    onSave={(val) => { handleUpdateField('startTime', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                                <InlineFieldEditor 
                                    label="Endzeit" 
                                    type="time"
                                    value={event.endTime || ''} 
                                    onSave={(val) => { handleUpdateField('endTime', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}

                        {(event.location || isEditable) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'rgba(var(--color-surface-on-variant), 0.8)' }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)', p: 0.6, borderRadius: 1, display: 'flex' }}>
                                    <LocationIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{event.location || 'Kein Ort angegeben'}</Typography>
                                {isEditable && <EditButton onClick={() => setEditingField('location')} />}
                            </Box>
                        )}

                        {isEditable && editingField === 'location' && (
                            <div className="my-2" onClick={(e) => e.stopPropagation()}>
                                <InlineFieldEditor 
                                    label="Ort" 
                                    value={event.location || ''} 
                                    onSave={(val) => { handleUpdateField('location', val); setEditingField(null); }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}

                        {isEditable && editingField === 'category' && (
                            <div className="my-2" onClick={(e) => e.stopPropagation()}>
                                <div className="p-4 rounded-2xl bg-surface-container border border-primary/20">
                                    <label className="text-label-medium font-bold text-primary uppercase block mb-2">Kategorie</label>
                                    <select 
                                        value={event.category}
                                        onChange={(e) => { handleUpdateField('category', e.target.value); setEditingField(null); }}
                                        className="w-full bg-surface-containerLowest border border-outlineVariant/30 rounded-xl px-4 py-3"
                                    >
                                        <option value="Gruppenstunde">Gruppenstunde</option>
                                        <option value="Lager">Lager</option>
                                        <option value="Aktion">Aktion</option>
                                        <option value="Sonstiges">Sonstiges</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {event.slug ? (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(var(--color-outline-variant), 0.15)', display: 'flex', justifyContent: 'flex-start' }} onClick={(e) => e.stopPropagation()}>
                                <Button
                                    variant="contained"
                                    color={categoryColor}
                                    size="small"
                                    href={`${MAIN_URL}/events/${event.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        borderRadius: 100,
                                        py: 1,
                                        px: 2.5,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        boxShadow: (theme) => `0 4px 10px -3px ${alpha(theme.palette[categoryColor].main, 0.3)}`,
                                        '&:hover': {
                                            boxShadow: (theme) => `0 6px 12px -3px ${alpha(theme.palette[categoryColor].main, 0.4)}`,
                                        }
                                    }}
                                >
                                    Details & Anmeldung →
                                </Button>
                            </Box>
                        ) : event.link ? (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(var(--color-outline-variant), 0.15)', display: 'flex', justifyContent: 'flex-start' }} onClick={(e) => e.stopPropagation()}>
                                <Button
                                    variant="contained"
                                    color={categoryColor}
                                    size="small"
                                    href={event.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        borderRadius: 100,
                                        py: 1,
                                        px: 2.5,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        boxShadow: (theme) => `0 4px 10px -3px ${alpha(theme.palette[categoryColor].main, 0.3)}`,
                                        '&:hover': {
                                            boxShadow: (theme) => `0 6px 12px -3px ${alpha(theme.palette[categoryColor].main, 0.4)}`,
                                        }
                                    }}
                                >
                                    Mehr Infos →
                                </Button>
                            </Box>
                        ) : null}
                    </Stack>
                </CardContent>
            </Card>

            {/* Expanded Dialog View */}
            <Dialog
                open={isExpanded}
                onClose={() => setIsExpanded(false)}
                maxWidth="sm"
                fullWidth
                TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
                PaperProps={{
                    sx: {
                        borderRadius: 6,
                        backgroundImage: 'none',
                        bgcolor: 'rgb(var(--color-surface-container-high))',
                        boxShadow: '0 24px 48px -12px rgba(0,0,0,0.25)',
                        position: 'relative',
                        overflow: 'hidden'
                    }
                }}
            >
                {/* Visual Category Indicator for Dialog */}
                <Box
                    sx={{
                        height: 6,
                        width: '100%',
                        bgcolor: (theme) => theme.palette[categoryColor].main,
                        position: 'absolute',
                        top: 0,
                        left: 0
                    }}
                />

                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 4, pb: 1, px: 4 }}>
                    <span className={`px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${
                        event.category === 'Lager'
                            ? 'bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40'
                            : event.category === 'Gruppenstunde'
                            ? 'bg-emerald-500/20 text-emerald-900 dark:text-emerald-200 border border-emerald-500/40'
                            : event.category === 'Aktion'
                            ? 'bg-sky-500/20 text-sky-900 dark:text-sky-200 border border-sky-500/40'
                            : 'bg-primary/20 text-primary border border-primary/40'
                    }`}>
                        {event.category || 'Event'}
                    </span>
                    <IconButton
                        onClick={() => setIsExpanded(false)}
                        sx={{
                            bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)',
                            '&:hover': { bgcolor: 'rgba(var(--color-surface-on-variant), 0.1)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 4, pb: 2 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: 'rgb(var(--color-surface-on))',
                            mb: 3,
                            lineHeight: 1.2
                        }}
                    >
                        {event.title}
                    </Typography>

                    <Stack spacing={2.5} sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                            <Box sx={{ bgcolor: (theme) => alpha(theme.palette[categoryColor].main, 0.1), p: 1.2, borderRadius: 2, display: 'flex' }}>
                                <CalendarIcon sx={{ fontSize: 24, color: (theme) => theme.palette[categoryColor].main }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(var(--color-surface-on-variant), 0.6)', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>Datum</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(event.date)}</Typography>
                            </Box>
                        </Box>

                        {(event.startTime || event.endTime) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)', p: 1.2, borderRadius: 2, display: 'flex' }}>
                                    <TimeIcon sx={{ fontSize: 24, color: 'rgba(var(--color-surface-on-variant), 0.8)' }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-surface-on-variant), 0.6)', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>Zeit</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {event.startTime} {event.endTime ? `- ${event.endTime}` : ''} Uhr
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {event.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={{ bgcolor: 'rgba(var(--color-surface-on-variant), 0.05)', p: 1.2, borderRadius: 2, display: 'flex' }}>
                                    <LocationIcon sx={{ fontSize: 24, color: 'rgba(var(--color-surface-on-variant), 0.8)' }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-surface-on-variant), 0.6)', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>Ort</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{event.location}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Stack>

                    {event.description && (
                        <Box
                            sx={{
                                bgcolor: 'rgba(var(--color-surface-container), 0.5)',
                                p: 3,
                                borderRadius: 4,
                                mb: 2,
                                border: '1px solid rgba(var(--color-outline-variant), 0.15)'
                            }}
                        >
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'rgba(var(--color-surface-on), 0.9)', lineHeight: 1.7 }}>
                                {event.description}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 4, pt: 1, flexDirection: 'column', gap: 2 }}>
                    <div className="flex w-full gap-3">
                        {event.slug ? (
                            <Button
                                href={`${MAIN_URL}/events/${event.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                color={categoryColor}
                                fullWidth
                                size="large"
                                sx={{
                                    borderRadius: 100,
                                    py: 1.8,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    boxShadow: (theme) => `0 8px 20px -6px ${alpha(theme.palette[categoryColor].main, 0.4)}`,
                                    '&:hover': {
                                        boxShadow: (theme) => `0 12px 24px -6px ${alpha(theme.palette[categoryColor].main, 0.5)}`,
                                    }
                                }}
                            >
                                Details & Anmeldung
                            </Button>
                        ) : event.link && (
                            <Button
                                href={event.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                color={categoryColor}
                                fullWidth
                                size="large"
                                sx={{
                                    borderRadius: 100,
                                    py: 1.8,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    boxShadow: (theme) => `0 8px 20px -6px ${alpha(theme.palette[categoryColor].main, 0.4)}`,
                                    '&:hover': {
                                        boxShadow: (theme) => `0 12px 24px -6px ${alpha(theme.palette[categoryColor].main, 0.5)}`,
                                    }
                                }}
                            >
                                Mehr Infos
                            </Button>
                        )}

                        <Button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const url = `${MAIN_URL}/events/${event.slug || ''}`;
                                if (navigator.share) {
                                    navigator.share({
                                        title: event.title,
                                        url: url
                                    }).catch(console.error);
                                } else {
                                    navigator.clipboard.writeText(url);
                                    alert('Link kopiert!');
                                }
                            }}
                            variant="outlined"
                            sx={{
                                borderRadius: 100,
                                minWidth: '120px',
                                border: '1.5px solid rgba(var(--color-outline), 0.3)',
                                color: 'rgba(var(--color-surface-on), 0.8)',
                                fontWeight: 600,
                                textTransform: 'none'
                            }}
                        >
                            Teilen
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EventCard;
